import React from "react";
import { XCircle, Sparkles } from "lucide-react";
import { formatCurrency, formatPercent } from "./dashboardUtils";
import type { Deal } from "../types";

interface LostDealsAndExtraServicesCardProps {
  deals: Deal[];
  lostStages: string[];
  wonStages: string[];
  currency?: string;
}

export const LostDealsAndExtraServicesCard: React.FC<
  LostDealsAndExtraServicesCardProps
> = ({ deals, lostStages, wonStages, currency = "BRL" }) => {
  // 1. Perdidos agrupados por Motivo de Perda
  const lostStats = React.useMemo(() => {
    const lostDeals = deals.filter((d) => {
      if (d.status === "perdido") return true;
      const stage = (d.stage || "").toLowerCase();
      return lostStages.some((s) => s.toLowerCase() === stage);
    });

    const reasons: Record<string, { count: number; amount: number }> = {};
    lostDeals.forEach((d) => {
      let r = (d.motivo_perda || "").trim();
      if (!r) r = "Motivo não informado";
      else {
        // Normaliza
        const lower = r.toLowerCase();
        if (
          lower.includes("preço") ||
          lower.includes("preco") ||
          lower.includes("caro")
        )
          r = "Preço / Honorários";
        else if (lower.includes("concorr")) r = "Fechou com Concorrente";
        else if (lower.includes("sem resposta") || lower.includes("sumiu"))
          r = "Sem Resposta / Contato Perdido";
        else if (
          lower.includes("timing") ||
          lower.includes("depois") ||
          lower.includes("momento")
        )
          r = "Timing / Não é o Momento";
        else if (lower.includes("fit") || lower.includes("perfil"))
          r = "Sem Fit / Perfil Inadequado";
        else r = r.charAt(0).toUpperCase() + r.slice(1);
      }

      if (!reasons[r]) reasons[r] = { count: 0, amount: 0 };
      reasons[r].count += 1;
      reasons[r].amount += Number(d.amount) || 0;
    });

    const totalLostCount = lostDeals.length;

    return {
      totalLostCount,
      totalLostAmount: lostDeals.reduce(
        (acc, d) => acc + (Number(d.amount) || 0),
        0,
      ),
      items: Object.entries(reasons)
        .map(([reason, data]) => ({
          reason,
          count: data.count,
          amount: data.amount,
          percent: totalLostCount > 0 ? (data.count / totalLostCount) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count),
    };
  }, [deals, lostStages]);

  // 2. Serviços Extras Vendidos
  const extraServicesStats = React.useMemo(() => {
    // Negócios ganhos que pertencem a serviços pontuais ou adicionais
    const wonDeals = deals.filter((d) => {
      if (d.status === "ganho") return true;
      const stage = (d.stage || "").toLowerCase();
      return wonStages.some((s) => s.toLowerCase() === stage);
    });

    // Mapeamento de categorias de serviços adicionais/extras
    const categoryLabels: Record<string, string> = {
      "bpo-financeiro": "BPO Financeiro",
      "abertura-empresa": "Abertura de Empresa",
      "recuperacao-tributaria": "Recuperação Tributária",
      consultoria: "Consultoria de Gestão",
      "planejamento-tributario": "Planejamento Tributário",
      auditoria: "Auditoria & Compliance",
      "transformacao-societaria": "Alteração Contratual / Societária",
      outro: "Outros Serviços Avulsos",
    };

    const serviceCounts: Record<string, { count: number; amount: number }> = {};

    wonDeals.forEach((d) => {
      const cat = (d.category || "").toLowerCase().trim();
      // Não conta contabilidade mensal pura como serviço extra
      if (cat && cat !== "contabilidade-mensal") {
        const label =
          categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
        if (!serviceCounts[label])
          serviceCounts[label] = { count: 0, amount: 0 };
        serviceCounts[label].count += 1;
        serviceCounts[label].amount += Number(d.amount) || 0;
      }
    });

    // Se nenhuma categoria de extra foi preenchida, verifica deals com descrição contendo extra/avulso
    if (Object.keys(serviceCounts).length === 0) {
      wonDeals.forEach((d) => {
        const desc = (d.name || "").toLowerCase();
        if (
          desc.includes("bpo") ||
          desc.includes("abertura") ||
          desc.includes("extra") ||
          desc.includes("avulso") ||
          desc.includes("recupera")
        ) {
          const label = "Serviços Avulsos / Extras";
          if (!serviceCounts[label])
            serviceCounts[label] = { count: 0, amount: 0 };
          serviceCounts[label].count += 1;
          serviceCounts[label].amount += Number(d.amount) || 0;
        }
      });
    }

    const items = Object.entries(serviceCounts)
      .map(([name, data]) => ({
        name,
        count: data.count,
        amount: data.amount,
      }))
      .sort((a, b) => b.amount - a.amount);

    const totalCount = items.reduce((acc, i) => acc + i.count, 0);
    const totalAmount = items.reduce((acc, i) => acc + i.amount, 0);

    return { items, totalCount, totalAmount };
  }, [deals, wonStages]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* 1. Perdidos agrupados por Motivo de Perda */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center">
              <XCircle className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Perdidos por Motivo
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Principais motivos de perda para análise de objeções
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400">
            {lostStats.totalLostCount} perdidos
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {lostStats.items.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              Nenhum negócio perdido registrado no período
            </div>
          ) : (
            lostStats.items.map((item) => (
              <div key={item.reason} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground truncate max-w-[200px]">
                    {item.reason}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-rose-600 dark:text-rose-400 font-semibold">
                      {item.count} ({formatPercent(item.percent)})
                    </span>
                    <span className="text-muted-foreground text-[10px]">
                      {formatCurrency(item.amount, currency)}
                    </span>
                  </div>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-rose-500 rounded-full transition-all duration-500"
                    style={{ width: `${item.percent}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 2. Serviço Extra Vendido */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Serviço Extra Vendido
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Serviços pontuais e consultorias vendidas nos projetos
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400">
            {formatCurrency(extraServicesStats.totalAmount, currency)}
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {extraServicesStats.items.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              Nenhum serviço extra vendido registrado ainda
            </div>
          ) : (
            extraServicesStats.items.map((svc) => (
              <div
                key={svc.name}
                className="flex items-center justify-between p-2 rounded-lg border border-border/40 bg-muted/20 text-xs"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="font-medium text-foreground">
                    {svc.name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-muted-foreground">
                    {svc.count} {svc.count === 1 ? "venda" : "vendas"}
                  </span>
                  <span className="font-bold text-foreground">
                    {formatCurrency(svc.amount, currency)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
